// Voice activity detection for Guess (energy based, adaptive noise floor, pure and testable).
//
// - speech starts after `startMs` of frames above the threshold;
// - it ends after `hangoverMs` below it; the END time reported is the last voiced frame,
//   so the clock stops when the player stopped talking, not when we noticed;
// - `preRollMs` of audio before the start is kept so the first syllable is not cut;
// - while `gate` is on (the app is playing a sound) no new utterance can start.

export interface VadConfig {
  startMs: number;
  hangoverMs: number;
  preRollMs: number;
  tailMs: number;
  maxMs: number;
  minVoicedMs: number;
  marginDb: number;
  minThresholdDb: number;
  maxThresholdDb: number;
}

export const DEFAULT_VAD: VadConfig = {
  startMs: 80,
  hangoverMs: 650,
  preRollMs: 300,
  tailMs: 200,
  maxMs: 6000,
  minVoicedMs: 140,
  marginDb: 10,
  minThresholdDb: -54,
  maxThresholdDb: -22,
};

export type VadEvent =
  | { t: "start"; at: number }
  | { t: "end"; startAt: number; endAt: number; frames: Float32Array[]; voicedMs: number; peakDb: number }
  | { t: "drop"; reason: "short" | "noise" };

/** Window for minimum statistics: speech always dips between syllables within it, steady noise never does. */
const MIN_WINDOW_MS = 1500;

export function rmsDb(frame: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  const rms = Math.sqrt(sum / Math.max(1, frame.length));
  return rms > 1e-7 ? 20 * Math.log10(rms) : -140;
}

interface Slot {
  f: Float32Array;
  end: number;
  ms: number;
}

export class Vad {
  cfg: VadConfig;
  noiseDb: number;
  level = -140;
  private speaking = false;
  private aboveMs = 0;
  private belowMs = 0;
  private ring: Slot[] = [];
  private frames: Slot[] = [];
  private startAt = 0;
  private lastVoicedEnd = 0;
  private voicedMs = 0;
  private peakDb = -140;
  private speechSum = 0;
  private speechN = 0;
  private lengths: number[] = [];
  /** Recent frame levels (dB) for the minimum-statistics noise estimate. */
  private hist: number[] = [];
  private histMs = 0;

  constructor(cfg: Partial<VadConfig> = {}, noiseDb = -58) {
    this.cfg = { ...DEFAULT_VAD, ...cfg };
    this.noiseDb = noiseDb;
  }

  get isSpeaking() {
    return this.speaking;
  }

  threshold(): number {
    return Math.min(this.cfg.maxThresholdDb, Math.max(this.cfg.minThresholdDb, this.noiseDb + this.cfg.marginDb));
  }

  /** Use a stored calibration (noise and speech level of this player's microphone). */
  calibrate(noiseDb: number, speechDb: number) {
    if (!Number.isFinite(noiseDb) || !Number.isFinite(speechDb) || speechDb <= noiseDb) return;
    this.noiseDb = noiseDb;
    this.cfg.marginDb = Math.max(6, Math.min(14, (speechDb - noiseDb) * 0.45));
  }

  calibration(): { noiseDb: number; speechDb: number; avgMs: number } | null {
    if (!this.speechN) return null;
    const avgMs = this.lengths.length ? this.lengths.reduce((a, b) => a + b, 0) / this.lengths.length : 0;
    return { noiseDb: Math.round(this.noiseDb * 10) / 10, speechDb: Math.round((this.speechSum / this.speechN) * 10) / 10, avgMs: Math.round(avgMs) };
  }

  /** Forget a running utterance (e.g. when the game stops listening). */
  reset() {
    this.speaking = false;
    this.aboveMs = 0;
    this.belowMs = 0;
    this.frames = [];
  }

  /** The quietest recent level: the noise floor of the room right now. */
  private minStat(): number {
    let m = Infinity;
    for (const v of this.hist) if (v < m) m = v;
    return m;
  }

  push(frame: Float32Array, endAt: number, frameMs: number, gate = false): VadEvent[] {
    const db = rmsDb(frame);
    this.level = db;
    const out: VadEvent[] = [];
    const thr = this.threshold();
    const slot: Slot = { f: frame, end: endAt, ms: frameMs };
    const maxHist = Math.max(1, Math.round(MIN_WINDOW_MS / frameMs));
    if (!gate) {
      this.hist.push(db);
      if (this.hist.length > maxHist) this.hist.shift();
      this.histMs = this.hist.length * frameMs;
    }

    if (!this.speaking) {
      this.ring.push(slot);
      let kept = 0;
      for (let i = this.ring.length - 1; i >= 0; i--) {
        kept += this.ring[i].ms;
        if (kept > this.cfg.preRollMs + this.cfg.startMs) {
          this.ring = this.ring.slice(i);
          break;
        }
      }
      if (gate) {
        // our own sound is playing: never start, and do not learn the noise floor from it
        this.aboveMs = 0;
        return out;
      }
      if (db > thr) {
        this.aboveMs += frameMs;
        if (this.aboveMs >= this.cfg.startMs) {
          this.speaking = true;
          this.frames = [...this.ring];
          this.startAt = endAt - this.aboveMs;
          this.lastVoicedEnd = endAt;
          this.voicedMs = this.aboveMs;
          this.peakDb = db;
          this.belowMs = 0;
          out.push({ t: "start", at: this.startAt });
        }
      } else {
        this.aboveMs = 0;
        // noise floor: falls fast; rises towards the quietest recent level (speech never drags it up,
        // because speech dips between syllables)
        if (db < this.noiseDb) this.noiseDb = this.noiseDb * 0.85 + db * 0.15;
        else if (this.histMs >= MIN_WINDOW_MS * 0.6) {
          const floor = this.minStat();
          if (floor > this.noiseDb) this.noiseDb += (floor - this.noiseDb) * 0.08;
        }
        this.noiseDb = Math.max(-90, Math.min(-25, this.noiseDb));
      }
      return out;
    }

    // A "voice" that never dips for a whole window is steady noise (fan, traffic, music bed):
    // drop it and learn it as the new floor.
    if (this.histMs >= MIN_WINDOW_MS && endAt - this.startAt >= MIN_WINDOW_MS) {
      const sorted = [...this.hist].sort((a, b) => a - b);
      const p10 = sorted[Math.floor(sorted.length * 0.1)];
      const p90 = sorted[Math.floor(sorted.length * 0.9)];
      const floor = p10;
      // speech is strongly modulated (syllables); a level that barely moves for 1.5 s is not a voice
      if (p90 - p10 < 6 && floor > thr - 3) {
        this.speaking = false;
        this.aboveMs = 0;
        this.ring = [];
        this.frames = [];
        this.noiseDb = Math.max(-90, Math.min(-25, floor));
        out.push({ t: "drop", reason: "noise" });
        return out;
      }
    }

    this.frames.push(slot);
    if (db > thr - 4) {
      this.lastVoicedEnd = endAt;
      this.belowMs = 0;
      if (db > thr) {
        this.voicedMs += frameMs;
        this.speechSum += db;
        this.speechN += 1;
      }
      this.peakDb = Math.max(this.peakDb, db);
    } else {
      this.belowMs += frameMs;
    }
    const dur = endAt - this.startAt;
    if (this.belowMs >= this.cfg.hangoverMs || dur >= this.cfg.maxMs) {
      this.speaking = false;
      this.aboveMs = 0;
      this.ring = [];
      if (this.voicedMs >= this.cfg.minVoicedMs) {
        const cut = this.lastVoicedEnd + this.cfg.tailMs;
        const frames = this.frames.filter((s) => s.end - s.ms <= cut).map((s) => s.f);
        this.lengths = [...this.lengths, this.lastVoicedEnd - this.startAt].slice(-10);
        out.push({ t: "end", startAt: this.startAt, endAt: this.lastVoicedEnd, frames, voicedMs: this.voicedMs, peakDb: this.peakDb });
      } else {
        out.push({ t: "drop", reason: "short" });
      }
      this.frames = [];
    }
    return out;
  }
}
